import type { PublicOrgArticle, PublicOrgAward } from "@/lib/publicOrganizationProfile";
import type { WorkModelView } from "@/lib/workModel";

/**
 * The org story page's view model (spec 2026-09-24 §6). One shape, filled by
 * two adapters: `fromPublicProfile` (anonymous RPC) and `fromSignedInOrg`
 * (the organizations row). Every story component reads this and nothing else.
 */

/** Reader-facing sections, in page order (spec §5.1). */
export type StorySectionKey =
  | "overview"
  | "offer"
  | "growth"
  | "operations"
  | "footprint"
  | "team"
  | "reviews"
  | "news";

export const STORY_SECTION_ORDER: readonly StorySectionKey[] = [
  "overview",
  "offer",
  "growth",
  "operations",
  "footprint",
  "team",
  "reviews",
  "news",
];

export type StoryAudience = "public" | "signedIn";

export interface StoryIdentity {
  orgId: string;
  slug: string | null;
  name: string;
  type: string | null;
  logoUrl: string | null;
  coverUrl: string | null;
  /** `profile_data.tagline`, else the `description` column. */
  tagline: string | null;
  city: string | null;
  state: string | null;
  foundedYear: number | null;
  website: string | null;
  isHiring: boolean;
}

/**
 * The editor's camelCase `profile_data` shape — the keys the existing section
 * components read. The public adapter fills only keys the anonymous RPC sends;
 * the signed-in adapter passes the row's `profile_data` through minus
 * OMITTED_PROFILE_KEYS. Deliberately loose: those components are untyped too.
 */
export type StoryProfileData = Record<string, unknown>;

/** The newsroom and awards the public RPC carries. Signed-in News fetches its own. */
export interface StoryNews {
  articles: PublicOrgArticle[];
  awards: PublicOrgAward[];
}

export interface OrgStoryView {
  audience: StoryAudience;
  identity: StoryIdentity;
  profileData: StoryProfileData;
  serviceTypes: string[];
  /** "City, ST" labels, one per office. */
  officeLabels: string[];
  /**
   * The cloud brokerage label as shown (src/lib/workModel.ts): the badge, and
   * whether the footprint is states only regardless of `officesDisplay`.
   * Public: from `work_model`; signed-in: the effective `workModel` and `is_managed`.
   */
  workModel: WorkModelView;
  /** Verified count when shown, else the declared count; null when neither is a positive number. */
  agentCount: number | null;
  /** True when agentCount is the verified count (get_org_verified_agent_count), shown only when the org opted in. */
  agentCountVerified: boolean;
  news: StoryNews | null;
  /** Discovery-call link. Signed-in only; the UI still gates it on FEATURES.showDiscoveryCalls. */
  calendlyUrl: string | null;
  /** The sections that render, in order. Decided once, by `storySections`. */
  sections: StorySectionKey[];
}

/** What an adapter builds before `storySections` runs. */
export type OrgStoryBase = Omit<OrgStoryView, "sections">;
